import React from "react";

import Figure from "react-bootstrap/Figure";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import BeastNavbar from "../components/BeastNavbar";
import { MathJax, MathJaxContext } from "better-react-mathjax";

import adsorptionExample1 from "../about_figures/adsorption-example-1.png";
import adsorptionExample2 from "../about_figures/adsorption-example-2.png";
import explicitWaterSize from "../about_figures/explicit-water-size.jpg";
import beastDbReactions from "../about_figures/BEAST-DB-reactions.png";

const AboutPage = () => {
  return (
    <>
      <BeastNavbar selected="about"/>
      <div className="wrapper">
      <Container>
        <MathJaxContext>
          <br></br>
          <h1 id="self-consistent-applied-potential-and-surface-charging">
            Calculation Standards
          </h1>
          <p>
            BEAST DB was created using the latest methodological techniques for
            modeling solvation, applied potential, and electronic structure.
            Data for the electrocatalytic systems contained within it will be
            routinely updated as new theory is developed. This page will also be
            updated with additional details as needed.{" "}
          </p>
          <p>
            Density functional theory (DFT) is the current workhorse technique
            used to generate data in BEAST DB, however we are actively
            developing approaches and workflows that facilitate beyond-DFT GW
            and RPA calculations to address some of its infamous shortcomings.
            The technical details used for calculations in the current iteration
            of BEAST DB are provided below. First though, we describe the
            framework that sets BEAST DB apart.{" "}
          </p>
          <h1 id="self-consistent-applied-potential-and-surface-charging">
            Self-consistent applied potential and surface charging
          </h1>
          <p>
            In a real electrochemical cell, the applied potential sets the
            electron chemical potential. The cell electrodes are metallic, thus
            allowing electrons to exchange with the reservoir and charge the
            surface. Depending on the Fermi energy (
            <MathJax inline={true}>{"\\(E_F\\)"}</MathJax>) of the surface
            relative to the applied potential, it may become either positively
            or negatively charged.{" "}
            <em>
              Accurately modeling this phenomena is key for describing
              electrocatalytic systems.
            </em>{" "}
          </p>

          <h2 id="canonical-ensemble-dft-energies">
            Canonical ensemble DFT energies
          </h2>
          <p>
            Over the past two decades, DFT free energies are perhaps most
            commonly calculated using a specified set of atoms, and thus number
            of electrons, in a system. In the{" "}
            <strong>canonical ensemble</strong>, the electron chemical potential
            can fluctuate but the total number of electrons in the system
            cannot. The DFT Helmholtz free energies of such calculations,{" "}
            <MathJax inline={true}>{"\\(G_{DFT}\\)"}</MathJax>, run with a
            broadening can be written as:
            <MathJax>
              {"\\(G_{DFT} = E_{DFT} - TS + E_{ZPE} + \\int^{T}_{0} C_{P}dT\\)"}
            </MathJax>{" "}
          </p>
          <p>
            where <MathJax inline={true}>{"\\(E_{DFT}\\)"}</MathJax> is the DFT
            internal energy, <MathJax inline={true}>{"\\(T\\)"}</MathJax> is
            temperature, <MathJax inline={true}>{"\\(S\\)"}</MathJax> is the
            electronic entropy,{" "}
            <MathJax inline={true}>{"\\(E_{ZPE}\\)"}</MathJax> is the zero-point
            energy, and <MathJax inline={true}>{"\\(C_P\\)"}</MathJax> is the
            constant pressure heat capacity. An additional term accounting for
            the proton concentration dependence of the entropy could be added to
            approximate a pH dependence however we omit this for the database
            (effectively assuming pH = 0) to allow users to apply any pH
            corrections they wish. These{" "}
            <MathJax inline={true}>{"\\(G_{DFT}\\)"}</MathJax> are not functions
            of applied potential however. To remedy this, the{" "}
            <a href="https://doi.org/10.1021/jp047349j">
              computational hydrogen electrode (CHE)
            </a>{" "}
            was developed and has been widely applied to study electrochemistry.{" "}
          </p>
          <p>
            The CHE sets the reference potential to be that of the standard
            hydrogen electrode (SHE). This relates the energy of a
            proton/electron pair (H<MathJax inline={true}>{"\\(^+\\)"}</MathJax>
            +e<MathJax inline={true}>{"\\(^-\\)"}</MathJax>) to the energy of{" "}
            <MathJax inline={true}>{"\\(\\frac{1}{2} H_2\\)"}</MathJax>. At an
            electrolyte pH = 0, 1 bar of gaseous H
            <MathJax inline={true}>{"\\(_2\\)"}</MathJax>, 298 K, and U = 0 V vs
            SHE, the following reaction is in equilibrium:
            <MathJax>
              {"\\(H_{2} \\rightleftharpoons 2(H^{+}+ e^{-})\\)"}
            </MathJax>
          </p>
          <p>
            For a reaction involving{" "}
            <MathJax inline={true}>{"\\(N_{PCET}\\)"}</MathJax> proton-coupled
            electron transfers (PCET):
            <MathJax>
              {
                "\\(Reactants + N_{PCET}(H^{+}+e^{-}) \\rightleftharpoons Products\\)"
              }
            </MathJax>
            the canonical ensemble DFT free energy of the state with the protons
            and electrons (the reactants in this example) can be modified to
            account for their energy at a specific applied potential using:
            <MathJax>{"\\(G_{DFT}[U] = G_{DFT} - N_{PCET}eU\\)"}</MathJax>
          </p>
          <p>
            where <MathJax inline={true}>{"\\(e\\)"}</MathJax> is the
            fundamental electron charge and{" "}
            <MathJax inline={true}>{"\\(U\\)"}</MathJax> is the applied
            potential vs SHE. The{" "}
            <MathJax inline={true}>{"\\(G_{DFT}\\)"}</MathJax> of the other
            state (the products in this example) is not modified.{" "}
          </p>
          <p>
            BEAST DB assumes that the vibrational contributions to{" "}
            <MathJax inline={true}>{"\\(G_{DFT}\\)"}</MathJax> are negligible
            for calculations involving surfaces due to error cancellation when
            taking adsorption energy differences between clean and adsorbed
            states. However, these corrections are applied for the molecular
            reference state energies (see section on molecular referencing
            below).
          </p>
          <p>
            BEAST DB is primarily focused on calculating and learning from grand
            canonical ensemble DFT energies, as described below, but includes{" "}
            <MathJax inline={true}>{"\\(G_{DFT}\\)"}</MathJax> for all systems
            to facilitate comparisons with existing literature.{" "}
          </p>
          <h2 id="grand-canonical-ensemble-dft-energies">
            Grand canonical ensemble DFT energies
          </h2>
          <p>
            Although the canonical CHE approach has been widely used in
            electrochemical DFT studies, it does not self-consistently account
            for ionic position changes with applied potential/surface charging
            and ensures that only reactions involving PCETs are considered.
            Additionally, energy differences between two different surface
            states likely will not have the same{" "}
            <MathJax inline={true}>{"\\(E_{F}\\)"}</MathJax>, meaning that
            canonical adsorption energies are missing a fundamental link between
            theory and experiment. These drawbacks have recently been studied
            more thoroughly in a wide{" "}
            <a href="https://doi.org/10.1021/jacs.8b10016">variety</a>{" "}
            <a href="https://doi.org/10.1021/acscatal.3c01978">of</a>{" "}
            <a href="https://doi.org/10.1021/acs.chemrev.1c00800">systems</a>.{" "}
          </p>
          <p>
            The <strong>grand canonical DFT (GCDFT)</strong> approach used here
            overcomes these limitations by self-consistently solving an
            auxiliary Hamiltonian such that direct free energy minimization of
            charged systems{" "}
            <a href="https://doi.org/10.1063/1.4978411">can be performed</a>. In
            grand canonical ensemble calculations, the applied potential is
            fixed while the number of system electrons,{" "}
            <MathJax inline={true}>{"\\(N\\)"}</MathJax>, is varied.{" "}
          </p>
          <p>
            GCDFT free energies,{" "}
            <MathJax inline={true}>{"\\(\\Phi[\\mu]\\)"}</MathJax>, are by
            definition functions of the applied potential and{" "}
            <MathJax inline={true}>{"\\(N\\)"}</MathJax>. They can be written
            as:
            <MathJax>{"\\(\\Phi[\\mu] = G_{DFT} - \\mu N\\)"}</MathJax>
          </p>
          <p>
            where <MathJax inline={true}>{"\\(\\mu\\)"}</MathJax> is the applied
            potential relative to vacuum and{" "}
            <MathJax inline={true}>{"\\(G_{DFT}\\)"}</MathJax> is calculated
            within the grand canonical ensemble for a system with{" "}
            <MathJax inline={true}>{"\\(N\\)"}</MathJax> electrons. For a system
            under an applied potential,{" "}
            <MathJax inline={true}>{"\\(U\\)"}</MathJax>, relative to a
            particular reference electrode,{" "}
            <MathJax inline={true}>{"\\(U_{Ref}\\)"}</MathJax>, the
            electrochemical potential vs vacuum is calculated using:
            <MathJax>{"\\( \\mu = -(U_{Ref} + U) \\)"}</MathJax>
          </p>
          <p>
            Although <MathJax inline={true}>{"\\(U_{Ref}\\)"}</MathJax> may be
            defined using the widely used standard hydrogen electrode value of
            4.44 eV, in this work we instead use{" "}
            <MathJax inline={true}>{"\\(U_{Ref}\\)"}</MathJax> = 4.66 eV, which
            is calibrated using the points of zero charge (PZC) of several
            transition metal surfaces for the{" "}
            <a href="https://doi.org/10.1063/1.4907731">
              CANDLE implicit solvation model
            </a>{" "}
            (see next section). If one is interested in calculations for{" "}
            <MathJax inline={true}>{"\\(U\\)"}</MathJax> = 1.0 V vs SHE, for
            example, this value is inserted into the above equation along with
            the desired <MathJax inline={true}>{"\\(U_{Ref}\\)"}</MathJax> to
            calculate the <MathJax inline={true}>{"\\(\\mu\\)"}</MathJax> value
            set in JDFTx (see the BEAST DB section below for additional
            details).
          </p>
          <p>
            It is important to note that GCDFT pairs best with the use of an
            implicit solvation model because is seamlessly compensates for the
            charge of a non-neutral surface and facilitates the use of auxiliary
            Hamiltonian method mentioned above.{" "}
          </p>
          <h1 id="implicit-solvation-models">Implicit solvation models</h1>
          <p>
            Although modeling solvent and solute molecules explicitly (i.e.
            quantum mechanically) is rigorously appealing and accounts for all
            solvent chemical interactions with the electrode and adsorbed
            species, including more than the first solvation shell adds
            significant cost to DFT calculations. Such calculations typically
            require relatively expensive molecular dynamics simulations to
            sample the large thermodynamic phase space of solvent
            configurations. For example, modeling 1 M KOH with 32 ion pairs
            would require about 1800 H
            <MathJax inline={true}>{"\\(_2\\)"}</MathJax>O molecules (Figure 1)!
            Using <i>ab-initio</i> molecular dynamics (time-stepped DFT) on
            systems of such size are also not tractable for most researchers.{" "}
            <br></br>
            <Figure.Image src={explicitWaterSize} alt="Explicit Water Size" />
            <br></br>
            Figure 1. Example of the large number of explicit water molecules
            required for physically relevant KOH concentrations.{" "}
          </p>
          <p>
            Implicit solvation approaches, which replace explicit solvent
            configurations with polarizable continua or liquid density
            distributions, are more computationally expedient and widely applied
            in conjunction with DFT in first-principles electrochemistry. These
            models bypass the phase-space sampling issue and capture the impact
            of the liquid in a single electronic structure calculation and are
            widely used in electrochemical DFT models.{" "}
          </p>
          <p>
            BEAST DB primarily uses the{" "}
            <a href="https://doi.org/10.1063/1.4907731">
              charge-asymmetric nonlocally determined local-electric (CANDLE)
              solvation model
            </a>{" "}
            for DFT calculations because is one of the most accurate models
            currently available for widespread use. It corrects well-known
            deficiencies in previous models to more accurately place the solvent
            cavity around a solute, captures charge asymmetry in solvation, and
            accurately predicts experimental quantities like molecular pKa
            values.{" "}
          </p>
          <p>
            The development of advanced implicit solvation models is ongoing and
            is a{" "}
            <a href="https://beast-echem.org/thrust1/">
              core thrust of BEAST research
            </a>
            .
          </p>
          <h1 id="beast-db-data-usage-and-interpretation">
            BEAST DB data, usage, and interpretation
          </h1>
          <h2 id="adsorption-energy-calculations">
            Adsorption energy calculations
          </h2>
          <p>
            For the below generic adsorption reaction of adsorbate,{" "}
            <MathJax inline={true}>{"\\(A\\)"}</MathJax>, to an open surface
            site, <MathJax inline={true}>{"\\(*\\)"}</MathJax>:
            <MathJax>{"\\(A + {*} \\rightarrow A^{*} \\)"}</MathJax>
          </p>
          <p>
            the grand canonical adsorption energy,{" "}
            <MathJax inline={true}>{"\\(\\Phi_{ads}[\\mu]\\)"}</MathJax>, can be
            written:
            <MathJax>
              {
                "\\( \\Phi_{ads}[\\mu] = \\Phi_{A^{*}}[\\mu] - (\\Phi_{Surf}[\\mu] + \\Phi_{MolcRef}[\\mu]) \\)"
              }
            </MathJax>
          </p>
          <p>
            where <MathJax inline={true}>{"\\(\\Phi_{A^{*}}[\\mu]\\)"}</MathJax>{" "}
            is the grand free energy of the adsorbed state (Figure 2B for
            example),{" "}
            <MathJax inline={true}>{"\\(\\Phi_{Surf}[\\mu]\\)"}</MathJax> is the
            grand free energy of the surface with an open site (Figure 2A for
            example), and{" "}
            <MathJax inline={true}>{"\\(\\Phi_{MolcRef}[\\mu]\\)"}</MathJax> is
            the total grand free energy of the unadsorbed molecular reference
            state, which can consist of multiple energies of separate molecular
            species (see Molecular referencing section). Here, more negative
            values indicate stronger adsorption.
          </p>
          <p>
            An example calculation for{" "}
            <MathJax inline={true}>{"\\({*}NH\\)"}</MathJax> adsorption on
            Cu(100) with an applied potential of 0 V vs SHE is shown here. Here,{" "}
            <MathJax inline={true}>{"\\(^{*}NH\\)"}</MathJax> is referenced to{" "}
            <MathJax inline={true}>
              {"\\(\\frac{1}{2} N_{2} + H^{+}\\)"}
            </MathJax>{" "}
            so that{" "}
            <MathJax inline={true}>
              {
                "\\(\\Phi_{^{*}NH}[\\mu] = \\frac{1}{2} \\Phi_{N_{2}}[\\mu] + \\Phi_{H^{+}}\\)"
              }
            </MathJax>
            . Note that the grand free energy of a proton is not
            potential-dependent because it has no electrons.{" "}
          </p>
          <table>
            <thead>
              <tr>
                <th>Term</th>
                <th>Grand free energy (eV)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <MathJax inline={true}>{"\\(\\Phi_{A^{*}}[\\mu]\\)"}</MathJax>
                </td>
                <td>-146162.624</td>
              </tr>
              <tr>
                <td>
                  <MathJax inline={true}>{"\\(\\Phi_{Surf}[\\mu]\\)"}</MathJax>
                </td>
                <td>-145902.188</td>
              </tr>
              <tr>
                <td>
                  <MathJax inline={true}>
                    {"\\(\\Phi_{MolcRef}[\\mu]\\)"}
                  </MathJax>
                </td>
                <td>-259.565</td>
              </tr>
              <tr>
                <td>
                  <MathJax inline={true}>{"\\(\\Phi_{ads}[\\mu]\\)"}</MathJax>
                </td>
                <td>-0.871</td>
              </tr>
            </tbody>
          </table>
          <p>
            A similar calculation using canonical free energies can also be
            performed.{" "}
          </p>
          <table>
            <thead>
              <tr>
                <th>Clean surface site</th>
                <th>
                  H<MathJax inline={true}>{"\\(^*\\)"}</MathJax> adsorbed state
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Figure.Image
                    style={{ width: "60%" }}
                    src={adsorptionExample1}
                    alt="Adsorption Example 1"
                  />
                </td>
                <td>
                  <Figure.Image
                    style={{ width: "60%" }}
                    src={adsorptionExample2}
                    alt="Adsorption Example 2"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <p>
            Figure 2. A) Clean Pt(111) 3x3 supercell expansion of Pt(111)
            surface without adsorbates. B) Pt(111) surface with H
            <MathJax inline={true}>{"\\(^{*}\\)"}</MathJax> adsorbate. Gray and
            white spheres represent Pt and H atoms, respectively.{" "}
          </p>
          <h2 id="reaction-pathway-energies">Reaction pathway energies</h2>
          <p>
            BEAST DB is currently focused on exploring the mechanism for four
            important electrocatalytic reaction pathways: the nitrogen reduction
            reaction (NRR), CO<MathJax inline={true}>{"\\(_2\\)"}</MathJax>{" "}
            reduction reaction (CO2RR), the hydrogen evolution reaction (HER),
            and the oxygen reduction reaction (ORR). We are working on expanding
            the scope of reactions and catalysts included in BEAST DB. The
            intermediates studied in BEAST DB are summarized in Figure 3 in
            black. The
            <MathJax inline={true}>{"\\(\\:^*\\)"}</MathJax> state denotes a
            clean surface without any adsorbates on it. A state labeled{" "}
            <MathJax inline={true}>{"\\(^* A\\)"}</MathJax> denotes an
            &quot;adsorbed system&quot; consisting of a catalyst with the{" "}
            <MathJax inline={true}>{"\\(A\\)"}</MathJax> species adsorbed to it.
            All other species in blue denote solution phase species. See the
            final section for details on calculating the molecular reference
            grand free energies.
            <Figure.Image src={beastDbReactions} alt="Adsorption Example 2" />
            Figure 3.
          </p>
          <p>
            The energies of states in a reaction pathway in BEAST DB are
            calculated similarly to the adsorption energy calculation of a
            molecule. The important difference is that now all states along the
            pathway are referenced to a single common final end point, the
            right-most state in the pathway. Each state along a pathway thus has
            the same types and numbers of atoms. For example,{" "}
            <MathJax inline={true}>
              {"\\(\\Phi_{A^{*}}^{i}[\\mu] + \\Phi_{MolcRef}^{i}[\\mu]\\)"}
            </MathJax>{" "}
            calculated for all states in the NRR pathway include energetic
            contributions from all catalyst atoms, 2 N atoms, and 6 H atoms. The
            energies used for all{" "}
            <MathJax inline={true}>{"\\(\\Phi_{A^{*}}^{i}[\\mu]\\)"}</MathJax>{" "}
            are given in black above while the energies used to
            stoichiometrically balance the state and calculated all{" "}
            <MathJax inline={true}>{"\\(\\Phi_{MolcRef}^{i}[\\mu]\\)"}</MathJax>{" "}
            are given in blue above.{" "}
          </p>
          <p>
            The grand free energy of a state{" "}
            <MathJax inline={true}>{"\\(i\\)"}</MathJax> along a reaction
            coordinate relative to the final state,{" "}
            <MathJax inline={true}>{"\\(\\Phi_{rxn}^{i}[\\mu]\\)"}</MathJax>, is
            written:
            <MathJax>
              {
                "\\( \\Phi_{rxn}^{i}[\\mu] = \\Phi_{A^{*}}^{i}[\\mu] + \\Phi_{MolcRef}^{i}[\\mu] - \\Phi_{Final}^{i}[\\mu] \\)"
              }
            </MathJax>
          </p>
          <p>
            The <MathJax inline={true}>{"\\(\\Phi_{rxn}^{i}[\\mu]\\)"}</MathJax>{" "}
            for the final state in the pathway is thus always 0 and the energy
            of the initial state represents the reaction energy at a given
            potential.
          </p>
          <p>
            An example calculation for{" "}
            <MathJax inline={true}>{"\\(^{*}NH\\)"}</MathJax> adsorption on
            Cu(100) with an applied potential of 1 V vs SHE is shown here. Here,{" "}
            <MathJax inline={true}>{"\\(^{*}NH\\)"}</MathJax> is referenced to{" "}
            <MathJax inline={true}>{"\\(2 NH_{3}\\)"}</MathJax>. Again, note
            that the grand free energy of a proton is not potential-dependent
            because it has no electrons.
          </p>
          <table>
            <thead>
              <tr>
                <th>Term</th>
                <th>Components</th>
                <th>Grand free energy (eV)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <MathJax inline={true}>
                    {"\\(\\Phi_{A^{*}}^{^*NH}[\\mu]\\)"}
                  </MathJax>
                </td>
                <td>
                  <MathJax inline={true}>
                    {"\\(\\Phi_{Cu(110)+^{*}NH}[\\mu]\\)"}
                  </MathJax>
                </td>
                <td>-146162.624</td>
              </tr>
              <tr>
                <td>
                  <MathJax inline={true}>
                    {"\\(\\Phi_{MolcRef}^{^*NH}[\\mu]\\)"}
                  </MathJax>
                </td>
                <td>
                  <MathJax inline={true}>
                    {"\\(2 \\Phi_{H^{+}} + \\Phi_{NH_{3}}[\\mu]\\)"}
                  </MathJax>
                </td>
                <td>-305.370</td>
              </tr>
              <tr>
                <td>
                  <MathJax inline={true}>
                    {"\\(\\Phi_{Final}^{^*NH}[\\mu]\\)"}
                  </MathJax>
                </td>
                <td>
                  <MathJax inline={true}>
                    {"\\(\\Phi_{Cu(110)}[\\mu] + 2 \\Phi_{NH_{3}}[\\mu]\\)"}
                  </MathJax>
                </td>
                <td>-146467.953</td>
              </tr>
              <tr>
                <td>
                  <MathJax inline={true}>
                    {"\\(\\Phi_{rxn}^{^*NH}[\\mu]\\)"}
                  </MathJax>
                </td>
                <td></td>
                <td>0.035</td>
              </tr>
            </tbody>
          </table>
          <p>
            A similar calculation using canonical free energies can also be
            performed.{" "}
          </p>
          <h2 id="molecular-reference-states">Molecular reference states</h2>
          <p>
            Importantly, the choice of molecular reference states is not unique.
            Different approaches can yield different adsorption energy
            predictions due to errors due to either DFT functional, solvation
            model, and/or other errors. For example, the OH
            <MathJax inline={true}>{"\\(^{-}\\)"}</MathJax> anion energy could
            be calculated directly or derived from the energy of
            <MathJax inline={true}>
              {"\\(\\: 1 \\:\\mathrm{H}_2\\mathrm{O}\\)"}
            </MathJax>{" "}
            minus the energy of a proton, and is greatly impacted by both
            functional and implicit solvation model chosen.{" "}
          </p>
          <p>
            We summarize the set of molecular references energies and the
            thermodynamic corrections included for each reference molecule used
            for BEAST DB below. All energies are in eV and the corrections were
            calculated at 298.15 K and 1 atm.
          </p>
          <table>
            <thead>
              <tr>
                <th>Molecule</th>
                <th>
                  <MathJax inline={true}>{"\\(E_{DFT}\\)"}</MathJax>
                </th>
                <th>TS</th>
                <th>
                  <MathJax inline={true}>{"\\(E_{ZPE}\\)"}</MathJax>
                </th>
                <th>
                  <MathJax inline={true}>{"\\(\\int C_{p}dT\\)"}</MathJax>
                </th>
                <th>
                  <MathJax inline={true}>{"\\(G_{DFT}\\)"}</MathJax>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  CH<MathJax inline={true}>{"\\(_4\\)"}</MathJax>
                </td>
                <td>-220.155</td>
                <td>0.576</td>
                <td>1.181</td>
                <td>0.105</td>
                <td>-219.445</td>
              </tr>
              <tr>
                <td>CO</td>
                <td>-591.723</td>
                <td>0.611</td>
                <td>0.136</td>
                <td>0.091</td>
                <td>-592.108</td>
              </tr>
              <tr>
                <td>
                  CO<MathJax inline={true}>{"\\(_2\\)"}</MathJax>
                </td>
                <td>-1030.827</td>
                <td>0.727</td>
                <td>0.270</td>
                <td>0.095</td>
                <td>-1031.190</td>
              </tr>
              <tr>
                <td>
                  H<MathJax inline={true}>{"\\(_2\\)"}</MathJax>
                </td>
                <td>-31.722</td>
                <td>0.404</td>
                <td>0.269</td>
                <td>0.091</td>
                <td>-31.767</td>
              </tr>
              <tr>
                <td>
                  H<MathJax inline={true}>{"\\(_2\\)"}</MathJax>O
                </td>
                <td>-470.240</td>
                <td>0.778</td>
                <td>0.568</td>
                <td>0.104</td>
                <td>-470.347</td>
              </tr>
              <tr>
                <td>
                  N<MathJax inline={true}>{"\\(_2\\)"}</MathJax>
                </td>
                <td>-543.176</td>
                <td>0.592</td>
                <td>0.150</td>
                <td>0.091</td>
                <td>-543.528</td>
              </tr>
              <tr>
                <td>
                  NH<MathJax inline={true}>{"\\(_3\\)"}</MathJax>
                </td>
                <td>-320.466</td>
                <td>0.742</td>
                <td>0.915</td>
                <td>0.105</td>
                <td>-320.189</td>
              </tr>
              <tr>
                <td>
                  O<MathJax inline={true}>{"\\(_2\\)"}</MathJax>
                </td>
                <td>-871.337</td>
                <td>0.740</td>
                <td>0.098</td>
                <td>0.091</td>
                <td>-871.890</td>
              </tr>
            </tbody>
          </table>
          <p>
            The energies of these species are added together to calculate the{" "}
            <MathJax inline={true}>{"\\(MolcRef\\)"}</MathJax> energies in
            either the adsorption energy and reaction pathway calculations and
            balance the energy difference calculation. The energy of atomic
            adsorbates such as H<MathJax inline={true}>{"\\(^+\\)"}</MathJax>,
            O, and N were calculated as half the energy of H
            <MathJax inline={true}>{"\\(_2\\)"}</MathJax>, O
            <MathJax inline={true}>{"\\(_2\\)"}</MathJax>, and N
            <MathJax inline={true}>{"\\(_2\\)"}</MathJax>, respectively. We note
            that the energy of a proton calculated in this manner is very close
            (within 0.02 eV) to calculating it using the equation:
            <MathJax>
              {
                "\\( \\Phi_{H^{+}}= \\Phi_{H_{3}O^{+}}[\\mu] - \\Phi_{H_{2}O}[\\mu] \\)"
              }
            </MathJax>
          </p>
          <p>
            where the grand free energies of H
            <MathJax inline={true}>{"\\(_3\\)"}</MathJax>O
            <MathJax inline={true}>{"\\(^{+}\\)"}</MathJax> and H
            <MathJax inline={true}>{"\\(_2\\)"}</MathJax>O have both been
            corrected using the same vibrational energy contributions shown
            above. We thus use this simpler approach.{" "}
          </p>
          <h1 id="beast-db-dft-standard-settings">
            BEAST DB DFT standard settings
          </h1>
          <p>
            Here we summarize the DFT standard used to generate current BEAST DB
            data. We have found that these settings give reliable results for a
            variety of electrocatalytic systems. Researchers wishing to
            contribute to BEAST DB should strictly adhere to these standards for
            the time being.{" "}
          </p>
          <p>
            All DFT calculations were run using the{" "}
            <a href="https://jdftx.org/">open-source JDFTx software package</a>.
            Note that JDFTx uses Ha energy units in the input file
            specification.{" "}
          </p>
          <p>
            Briefly, DFT used the PBE GGA exchange-correlation functional, the
            Grimme D3 van der Waals correction, GBRV ultrasoft pseudopotentials
            with a planewave cutoff of 20 Ha (40 Ryd{" "}
            <MathJax inline={true}>{"\\(\\approx\\)"}</MathJax> 544 eV). 100 Ha
            (200 Ryd <MathJax inline={true}>{"\\(\\approx\\)"}</MathJax> 2721
            eV) was used as a cutoff for the charge density. They also used a
            k-point density of 1000 per reciprocal atom with{" "}
            <MathJax inline={true}>{"\\(k_z\\)"}</MathJax> = 1 for surfaces,
            Coulomb interaction truncation along any aperiodic directions in the
            structures, Fermi occupation smearing with a broadening of 0.001 Ha
            (0.002 Ryd <MathJax inline={true}>{"\\(\\approx\\)"}</MathJax> 0.027
            eV), spin-polarization, CANDLE implicit solvation using water, and
            0.5 M NaF as the ion concentration (note that the identity of the
            ions do not matter and NaF should always be used). Note that
            broadening in JDFTx uses an exponential factor,{" "}
            <MathJax inline={true}>{"\\(x\\)"}</MathJax>, in the various
            broadening schemes of:
            <MathJax>{"\\( x = (\\epsilon_{nk}-E_{F})/(2\\sigma) \\)"}</MathJax>
          </p>
          <p>
            where <MathJax inline={true}>{"\\(\\epsilon_{nk}\\)"}</MathJax> is
            the eigenvalue for the{" "}
            <MathJax inline={true}>{"\\(n^{th}\\)"}</MathJax> band and{" "}
            <MathJax inline={true}>{"\\(k^{th}\\)"}</MathJax> k-point and{" "}
            <MathJax inline={true}>{"\\(\\sigma\\)"}</MathJax> is the broadening
            width. The system potential,{" "}
            <MathJax inline={true}>{"\\(\\mu\\)"}</MathJax>, was calculated
            using the equations described above.{" "}
          </p>
          <p>
            Solution phase species were run using Coulomb truncation along all
            three lattice vectors at the{" "}
            <MathJax inline={true}>{"\\(\\Gamma\\)"}</MathJax> point without
            applied potential. The{" "}
            <MathJax inline={true}>{"\\(-\\mu N\\)"}</MathJax> potential
            dependence of these molecules was then applied as a post-processing
            correction using the linear free energy approximation, which assumes
            that molecules do not become charged under applied potentials
            relevant to electrocatalysis.{" "}
          </p>
          <p>
            An example JDFTx input file for NH adsorbed to Cu(100) is provided
            below:
          </p>
          <pre>
            <code>
              lattice \<span className="hljs-number">14.303927</span>{" "}
              <span className="hljs-number">0.002183</span>{" "}
              <span className="hljs-number">0.000000</span> \ <br></br>
              <span className="hljs-number">0.000000</span>{" "}
              <span className="hljs-number">14.308315</span>{" "}
              <span className="hljs-number">0.000000</span> \ <br></br>
              <span className="hljs-number">0.000334</span>{" "}
              <span className="hljs-number">0.000357</span>{" "}
              <span className="hljs-number">47.156528</span> <br></br>
              <br></br>
              dump-name $VAR <br></br>
              initial-<span className="hljs-section">state</span> $VAR <br></br>
              kpoint-folding <span className="hljs-number">4</span>{" "}
              <span className="hljs-number">4</span>{" "}
              <span className="hljs-number">1</span> <br></br>
              elec-cutoff <span className="hljs-number">20</span>{" "}
              <span className="hljs-number">100</span> <br></br>
              elec-ex-corr gga <br></br>
              van-der-waals D3 <br></br>
              elec-n-bands <span className="hljs-number">311</span> <br></br>
              electronic-minimize nIterations{" "}
              <span className="hljs-number">100</span> energyDiffThreshold{" "}
              <span className="hljs-number">1e-07</span> <br></br>
              elec-smearing Fermi <span className="hljs-number">
                0.001
              </span>{" "}
              <br></br>
              elec-initial-magnetization <span className="hljs-number">
                0
              </span>{" "}
              no <br></br>
              spintype z-spin <br></br>
              core-overlap-check none <br></br>
              converge-empty-states yes <br></br>
              latt-move-scale <span className="hljs-number">0</span>{" "}
              <span className="hljs-number">0</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              lattice-minimize nIterations{" "}
              <span className="hljs-number">00</span> <br></br>
              symmetries none <br></br>
              fluid LinearPCM <br></br>
              pcm-variant CANDLE <br></br>
              fluid-solvent H2O <br></br>
              fluid-cation Na+ <span className="hljs-number">0.5</span>{" "}
              <br></br>
              fluid-anion F- <span className="hljs-number">0.5</span> <br></br>
              dump End Dtot BoundCharge <br></br>
              dump End State <br></br>
              dump End Forces <br></br>
              dump End Ecomponents <br></br>
              dump End VfluidTot <br></br>
              dump End ElecDensity <br></br>
              dump End KEdensity <br></br>
              dump End EigStats <br></br>
              dump End BandEigs <br></br>
              dump End DOS <br></br>
              target-mu <span className="hljs-number">-0.1713</span> <br></br>
              <br></br>
              coords-type cartesian <br></br>
              ion Cu <span className="hljs-number">-0.000026</span>{" "}
              <span className="hljs-number">-0.000039</span>{" "}
              <span className="hljs-number">25.268300</span>{" "}
              <span className="hljs-number">1</span> <br></br>
              ion Cu <span className="hljs-number">0.000727</span>{" "}
              <span className="hljs-number">4.771204</span>{" "}
              <span className="hljs-number">25.221401</span>{" "}
              <span className="hljs-number">1</span> <br></br>
              ion Cu <span className="hljs-number">0.001319</span>{" "}
              <span className="hljs-number">9.537028</span>{" "}
              <span className="hljs-number">25.221506</span>{" "}
              <span className="hljs-number">1</span> <br></br>
              ion Cu <span className="hljs-number">4.769791</span>{" "}
              <span className="hljs-number">-0.000013</span>{" "}
              <span className="hljs-number">25.221526</span>{" "}
              <span className="hljs-number">1</span> <br></br>
              ion Cu <span className="hljs-number">4.738509</span>{" "}
              <span className="hljs-number">4.739201</span>{" "}
              <span className="hljs-number">25.406995</span>{" "}
              <span className="hljs-number">1</span> <br></br>
              ion Cu <span className="hljs-number">4.738997</span>{" "}
              <span className="hljs-number">9.569321</span>{" "}
              <span className="hljs-number">25.407082</span>{" "}
              <span className="hljs-number">1</span> <br></br>
              ion Cu <span className="hljs-number">9.534078</span>{" "}
              <span className="hljs-number">-0.000148</span>{" "}
              <span className="hljs-number">25.221624</span>{" "}
              <span className="hljs-number">1</span> <br></br>
              ion Cu <span className="hljs-number">9.567094</span>{" "}
              <span className="hljs-number">4.738954</span>{" "}
              <span className="hljs-number">25.407078</span>{" "}
              <span className="hljs-number">1</span> <br></br>
              ion Cu <span className="hljs-number">9.567598</span>{" "}
              <span className="hljs-number">9.569085</span>{" "}
              <span className="hljs-number">25.407307</span>{" "}
              <span className="hljs-number">1</span> <br></br>
              ion Cu <span className="hljs-number">0.000014</span>{" "}
              <span className="hljs-number">0.000000</span>{" "}
              <span className="hljs-number">18.525772</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">0.000742</span>{" "}
              <span className="hljs-number">4.769433</span>{" "}
              <span className="hljs-number">18.525891</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">0.001469</span>{" "}
              <span className="hljs-number">9.538881</span>{" "}
              <span className="hljs-number">18.526010</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">4.767985</span>{" "}
              <span className="hljs-number">0.000000</span>{" "}
              <span className="hljs-number">18.525884</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">4.768713</span>{" "}
              <span className="hljs-number">4.769433</span>{" "}
              <span className="hljs-number">18.526003</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">4.769440</span>{" "}
              <span className="hljs-number">9.538881</span>{" "}
              <span className="hljs-number">18.526122</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">9.535956</span>{" "}
              <span className="hljs-number">0.000000</span>{" "}
              <span className="hljs-number">18.525995</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">9.536683</span>{" "}
              <span className="hljs-number">4.769433</span>{" "}
              <span className="hljs-number">18.526114</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">9.537411</span>{" "}
              <span className="hljs-number">9.538881</span>{" "}
              <span className="hljs-number">18.526233</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">2.384356</span>{" "}
              <span className="hljs-number">2.384710</span>{" "}
              <span className="hljs-number">21.894231</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">2.385084</span>{" "}
              <span className="hljs-number">7.154157</span>{" "}
              <span className="hljs-number">21.894350</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">2.385811</span>{" "}
              <span className="hljs-number">11.923591</span>{" "}
              <span className="hljs-number">21.894469</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">7.152341</span>{" "}
              <span className="hljs-number">2.384710</span>{" "}
              <span className="hljs-number">21.894343</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">7.153069</span>{" "}
              <span className="hljs-number">7.154157</span>{" "}
              <span className="hljs-number">21.894462</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">7.153796</span>{" "}
              <span className="hljs-number">11.923591</span>{" "}
              <span className="hljs-number">21.894581</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">11.920312</span>{" "}
              <span className="hljs-number">2.384710</span>{" "}
              <span className="hljs-number">21.894454</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">11.921040</span>{" "}
              <span className="hljs-number">7.154157</span>{" "}
              <span className="hljs-number">21.894573</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion Cu <span className="hljs-number">11.921767</span>{" "}
              <span className="hljs-number">11.923591</span>{" "}
              <span className="hljs-number">21.894692</span>{" "}
              <span className="hljs-number">0</span> <br></br>
              ion N <span className="hljs-number">7.153011</span>{" "}
              <span className="hljs-number">7.154103</span>{" "}
              <span className="hljs-number">27.029717</span>{" "}
              <span className="hljs-number">1</span> <br></br>
              ion H <span className="hljs-number">7.152989</span>{" "}
              <span className="hljs-number">7.154084</span>{" "}
              <span className="hljs-number">28.983191</span>{" "}
              <span className="hljs-number">1</span> <br></br>
              <br></br>
              ion-species GBRV/$ID_pbe.uspp <br></br>
              <br></br>
              coulomb-interaction slab <span className="hljs-number">
                001
              </span>{" "}
              <br></br>
              coulomb-truncation-embed{" "}
              <span className="hljs-number">5.67311</span>{" "}
              <span className="hljs-number">5.67397</span>{" "}
              <span className="hljs-number">22.3303</span> <br></br>
              dump End Forces <br></br>
              dump End Ecomponents <br></br>
            </code>
          </pre>
          <p>
            All surfaces were generated by first optimizing the lattice vectors
            and ionic positions of bulk materials downloaded from Materials
            Project if possible. Symmetrized surfaces were generated by cleaving
            these bulk materials along various Miller indices, ensuring that the
            slabs are thick enough to yield converged results, and freezing the
            lattice vectors. The lower layers of the slabs are frozen at their
            bulk positions. 15 Å of vacuum space was used and the minimum slab
            widths along the <MathJax inline={true}>{"\\(\\vec{a}\\)"}</MathJax>{" "}
            and <MathJax inline={true}>{"\\(\\vec{b}\\)"}</MathJax> lattice
            vectors was 7 Å. Ionic optimization of the canonical ensemble
            surfaces are first performed followed by ionic optimization at the
            each applied potential of interest in the grand canonical ensemble.{" "}
          </p>
          <p>
            Adsorbed states were generated by initializing adsorbates 2 Å above
            a surface on unique surfaces sites. The canonical ensemble ionic
            positions are fully optimized and used as starting points for grand
            canonical ensemble ionic optimizations.{" "}
          </p>
        </MathJaxContext>
      </Container>
      </div>
    </>
  );
};

export default AboutPage;
